import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogIn, UserPlus, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '' });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) { toast.error('Enter your email'); return; }
    setForgotSending(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      toast.success('Reset link sent! Check your email.');
      setShowForgot(false); setForgotEmail('');
    } catch (e) { toast.error('Failed to send reset link'); }
    setForgotSending(false);
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await api.post('/auth/login', loginForm);
      const { user, session_token, onboarding_status } = response.data;
      
      // Store session token
      localStorage.setItem('session_token', session_token);
      
      // If onboarding is incomplete, redirect to onboard page
      if (onboarding_status && onboarding_status !== 'complete') {
        toast.info('Please complete your onboarding first');
        navigate('/onboard');
        return;
      }
      
      // Redirect based on role
      let redirectPath = '/translator';
      if (user.role === 'admin') {
        redirectPath = '/admin';
      } else if (user.role === 'owner') {
        redirectPath = '/owner';
      }
      
      toast.success(`Welcome, ${user.name}!`);
      navigate(redirectPath, { state: { user } });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await api.post('/auth/register', registerForm);
      const { user, session_token } = response.data;
      
      // Store session token
      localStorage.setItem('session_token', session_token);
      
      toast.success('Registration successful!');
      navigate('/translator', { state: { user } });
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex">
      {/* Left Side - Hero */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-creative/80" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h1 className="text-5xl font-heading font-bold mb-4">
            Welcome to<br />Graphikpro Portal
          </h1>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Streamline your translation workflow with intelligent project management,
            automated invoicing, and seamless collaboration.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Secure NDA Management</h3>
                <p className="text-sm text-white/80">Digital signatures with full audit trail</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Automated Invoicing</h3>
                <p className="text-sm text-white/80">TDS calculation and PDF generation</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Real-time Tracking</h3>
                <p className="text-sm text-white/80">Monitor work progress and payments</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right Side - Login/Register Forms */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h2 className="text-4xl font-heading font-bold text-primary mb-2">
              Sign In
            </h2>
            <p className="text-muted-foreground">
              Access your translator portal
            </p>
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <Tabs defaultValue="login" className="space-y-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
                </TabsList>
                
                {/* Login Tab */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login_email">Email</Label>
                      <Input
                        id="login_email"
                        type="email"
                        placeholder="your@email.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                        required
                        data-testid="input-login-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login_password">Password</Label>
                      <Input
                        id="login_password"
                        type="password"
                        placeholder="••••••••"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                        required
                        data-testid="input-login-password"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      size="lg"
                      className="w-full bg-accent hover:bg-accent/90"
                      data-testid="btn-login-submit"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Logging in...
                        </>
                      ) : (
                        <>
                          <LogIn className="w-5 h-5 mr-2" />
                          Login
                        </>
                      )}
                    </Button>
                    <div className="text-center">
                      <button type="button" onClick={() => setShowForgot(true)} className="text-sm text-violet-600 hover:underline" data-testid="forgot-password-link">
                        Forgot your password?
                      </button>
                    </div>
                  </form>
                </TabsContent>
                
                {/* Register Tab */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register_name">Full Name</Label>
                      <Input
                        id="register_name"
                        placeholder="John Doe"
                        value={registerForm.name}
                        onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                        required
                        data-testid="input-register-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register_email">Email</Label>
                      <Input
                        id="register_email"
                        type="email"
                        placeholder="your@email.com"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                        required
                        data-testid="input-register-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register_password">Password</Label>
                      <Input
                        id="register_password"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                        required
                        data-testid="input-register-password"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      size="lg"
                      className="w-full bg-creative hover:bg-creative/90"
                      data-testid="btn-register-submit"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Creating account...
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-5 h-5 mr-2" />
                          Create Account
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          
          <div className="text-center text-sm text-muted-foreground">
            <p>Need help? Contact your administrator</p>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Enter your email and we'll send you a password reset link.</p>
            <Input value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="your@email.com" type="email" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForgot(false)}>Cancel</Button>
            <Button onClick={handleForgotPassword} disabled={forgotSending}>{forgotSending ? 'Sending...' : 'Send Reset Link'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
